import InfoIcon from '@mui/icons-material/Info';
import LaunchIcon from '@mui/icons-material/Launch';
import { Button, type Theme, Typography } from '@mui/material';
import MuiButton from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import { Component, Fragment } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import DPS from '/@/components/bag-quality/dps';
import Container from '/@/components/container';
import Icon from '/@/components/icon';
import Link from '/@/components/link';
import settings from '/@/core/settings';
import QRCodeScanner, { checkVideoInput } from '/@/hooks/qrcode-scanner';
import { _ } from '/@/locale';
import {
    fetchDataByBatch,
    fetchDataByQRCode,
    fetchHistoricalConsultations,
    resetBatchData,
    saveBatchConsultation,
    setBatchData,
    setHistoricalConsultations,
} from '/@/store/bag-quality/actions';
import {
    getActiveBrands,
    getBatchData,
    getHistoricalConsultations,
    getQrCornSeedQualityEnabled,
} from '/@/store/bag-quality/selectors';
import { getInternalProfileEnabled, getSessionCountry, getUser } from '/@/store/session/selectors';
import { trackEvent } from '/@/store/tracking';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import { isMobile } from '/@/utils';
import { reverse } from '/@/utils/router';
import Title from '/@/v3/components/title';
import Tooltip from '/@/v3/components/tooltip';
import dps from './assets/dps.svg?url';
import styles from './styles.module.css';

@connect(
    (state) => {
        return {
            brands: getActiveBrands(state, 'corn'),
            brand: getActiveBrands(state, 'corn')[0], //TODO: BRAND
            historicalConsultations: getHistoricalConsultations(state),
            batchData: getBatchData(state),
            internalProfileEnabledOrLT: getInternalProfileEnabled(state) || settings.app.theme === 'latijereta',
            country: getSessionCountry(state),
            user: getUser(state),
            qrCornSeedQualityEnabled: getQrCornSeedQualityEnabled(state),
        };
    },
    (dispatch) =>
        bindActionCreators(
            {
                setHistoricalConsultations,
                setBatchData,
                resetBatchData,
                fetchDataByBatch,
                saveBatchConsultation,
                fetchHistoricalConsultations,
                fetchDataByQRCode,
                trackEvent,
            },
            dispatch
        )
)
@autobind
export default class BagQuality extends Component {
    state = {
        batchInputValue: '',
        noteInputValue: '',
        batchError: false,
        showHistorical: false,
        disableBtn: true,
        showDPSAnalysis: false,
        showQRScanner: false,
    };

    async UNSAFE_componentWillMount() {
        if (this.props.user && this.props.brand?.id) {
            try {
                const data = await this.props.fetchHistoricalConsultations({ brand: this.props.brand.id });
                if (data?.response) {
                    this.props.setHistoricalConsultations(data.response);
                }
            } catch (error) {
                console.error('Error fetching historical consultations:', error);
                // Don't break the UI, just log the error
            }
        }
        try {
            const hasVideoInput = await checkVideoInput();
            this.setState({ showQRScanner: hasVideoInput });
        } catch (error) {
            console.error('Error checking video input:', error);
            this.setState({ showQRScanner: false });
        }
    }

    onNewScanResult = (decodedObj) => {
        this.getDataByQRCode(decodedObj);
    };

    scrollToBottom = () => {
        this.dpsEnd.scrollIntoView({ behavior: 'smooth' });
    };

    scrollToBatchInfo = () => {
        this.batchInfo.scrollIntoView({ behavior: 'smooth' });
    };

    handleBatchChange(event) {
        this.setState({ batchInputValue: event.target.value });
    }

    handleNoteChange(event) {
        this.setState({ noteInputValue: event.target.value });
    }

    async getDataByQRCode(qrCode) {
        this.setState({ showDPSAnalysis: false });

        try {
            const result = await this.props.fetchDataByQRCode({
                qrCode,
                brand: this.props.brand?.id,
            });

            if (!result) {
                this.setState({ batchError: true, disableBtn: false });
                return;
            }

            const { response, data } = result;
            this.props.setBatchData(data);
            this.setState({ batchError: response?.status !== 200 });
            if (response?.status === 200) {
                this.scrollToBatchInfo();
            }
            this.setState({ disableBtn: false });
        } catch (error) {
            console.error('Error fetching data by QR code:', error);
            this.setState({ batchError: true, disableBtn: false });
            this.props.setBatchData(undefined);
        }
    }

    async onSubmit(event) {
        event?.preventDefault();
        this.setState({ showDPSAnalysis: false });

        try {
            const result = await this.props.fetchDataByBatch({
                batch: this.state.batchInputValue.toUpperCase(),
                brand: this.props.brand?.id,
            });

            if (!result) {
                this.setState({ batchError: true, disableBtn: false });
                mpTrackEvent('Seed quality inquiry submitted', {
                    state: 'fail',
                    search: this.state.batchInputValue,
                    brand: this.props.brand?.name,
                });
                return;
            }

            const { response, data } = result;
            if (response?.status === 200) {
                mpTrackEvent('Seed quality inquiry submitted', {
                    state: 'success',
                    search: this.state.batchInputValue,
                    brand: this.props.brand?.name,
                });
                this.props.trackEvent({
                    key: 'bag-quality.search-success',
                    payload: {
                        search: this.state.batchInputValue || '',
                        brand: this.props.brand?.name || '',
                    },
                });
            } else {
                mpTrackEvent('Seed quality inquiry submitted', {
                    state: 'fail',
                    search: this.state.batchInputValue,
                    brand: this.props.brand?.name,
                });
                this.props.trackEvent({
                    key: 'bag-quality.search-error',
                    payload: {
                        search: this.state.batchInputValue || '',
                        brand: this.props.brand?.name || '',
                    },
                });
            }
            this.props.setBatchData(data);
            this.setState({ batchError: response?.status !== 200 });
            if (response?.status === 200) {
                this.scrollToBatchInfo();
            }
            this.setState({ disableBtn: false });
        } catch (error) {
            console.error('Error submitting batch inquiry:', error);
            this.setState({ batchError: true, disableBtn: false });
            this.props.setBatchData(undefined);
            mpTrackEvent('Seed quality inquiry submitted', {
                state: 'fail',
                search: this.state.batchInputValue,
                brand: this.props.brand?.name,
            });
            this.props.trackEvent({
                key: 'bag-quality.search-error',
                payload: {
                    search: this.state.batchInputValue || '',
                    brand: this.props.brand?.name || '',
                },
            });
        }
    }

    async handleSeeBatchHistorical(batch) {
        try {
            const result = await this.props.fetchDataByBatch({
                batch: batch.toUpperCase(),
                brand: this.props.brand?.id,
            });

            if (!result) {
                this.setState({ batchError: true, batchInputValue: batch });
                return;
            }

            const { response, data } = result;
            this.props.setBatchData(data);
            this.setState({ batchError: response?.status !== 200, batchInputValue: batch });
            if (response?.status === 200) {
                this.scrollToBatchInfo();
            }
        } catch (error) {
            console.error('Error fetching batch historical data:', error);
            this.setState({ batchError: true, batchInputValue: batch });
            this.props.setBatchData(undefined);
        }
    }

    async showSavedHistorical() {
        this.setState({ showHistorical: !this.state.showHistorical });
    }

    handleKeyDown(e) {
        if (e.keyCode === 13) {
            this.handleSaveBatch();
        }
    }

    handleKeyDownNoAction(e) {
        if (e.keyCode === 13) {
            e.preventDefault();
        }
    }

    async handleSaveBatch() {
        if (!this.props.batchData?.response?.id) {
            console.error('Cannot save batch: missing batch data or ID');
            return;
        }

        try {
            await this.props.saveBatchConsultation({
                id: this.props.batchData.response.id,
                note: this.state.noteInputValue,
            });
            mpTrackEvent('Seed quality inquiry saved', { has_note: !!this.state.noteInputValue });
            this.props.resetBatchData();
            this.setState({
                noteInputValue: '',
                batchInputValue: '',
                error: false,
                disableBtn: true,
                showDPSAnalysis: false,
            });

            try {
                const data = await this.props.fetchHistoricalConsultations({ brand: this.props.brand?.id });
                if (data?.response) {
                    this.props.setHistoricalConsultations(data.response);
                }
            } catch (error) {
                console.error('Error refreshing historical consultations:', error);
                // Don't break the UI, saving was successful
            }
        } catch (error) {
            console.error('Error saving batch consultation:', error);
            // Keep the UI state intact, user can try again
        }
    }

    renderHeader() {
        return (
            <div className={styles.header}>
                <div className={styles.containerHeader}>
                    {['cultivio', 'expertobayer'].includes(settings.app.theme) && this.renderDPS(true)}
                </div>
            </div>
        );
    }

    renderTitle() {
        return (
            <div className={styles.title}>
                <Title theme={settings.app.theme} bolder className={styles.titleStyle}>
                    {_('Calidad de Semilla Maíz')}
                </Title>
                <Title color='dark' className={styles.subtitleStyle}>
                    {this.props.internalProfileEnabledOrLT
                        ? _(
                              'Ingresá los datos del Lote de la bolsa de maíz de tu cliente y accedé a la información de calidad y obtené la recomendación de placa para tu híbrido/grado.'
                          )
                        : _(
                              'Ingresá los datos del lote de la bolsa de maíz y obtené la recomendación de placa para tu híbrido / grado.'
                          )}
                </Title>
            </div>
        );
    }

    renderDPS(value) {
        // DPS program is not available in Uruguay — hide all DPS content there.
        if (this.props.country?.code === 'UY') {
            return null;
        }

        const slugServicioPostventa = { slug: 'dps-servicio-postventa' };
        const slugProbadorDePlaca = { slug: 'dps-probador-placa' };

        const isHeader = value;
        const className = isHeader ? styles.containerHeaderDps : styles.containerFooterDps;
        return (
            <DPS className={className}>
                {isHeader ? (
                    !isMobile() ? (
                        <div>
                            <Typography variant='body1' sx={{ color: 'white !important', mb: 1 }}>
                                {_('¿Ya te enteraste de los beneficios del programa DPS?')}
                            </Typography>
                            <Button variant='text' size='small' color='white' onClick={this.scrollToBottom}>
                                {_('Conocer más')}
                            </Button>
                        </div>
                    ) : (
                        <Button
                            variant='text'
                            sx={{ color: 'white !important', fontSize: '7px', mb: 2 }} //custom fontSize to match the PDS card
                            onClick={this.scrollToBottom}
                        >
                            {_('¿Ya te enteraste de los beneficios\ndel programa DPS?')}
                        </Button>
                    )
                ) : (
                    <div>
                        <Typography variant='body1' sx={{ color: 'white !important' }}>
                            {_(
                                'Recordá que DEKALB cuenta con el programa DPS que te permite ofrecerle a tu cliente un análisis de poder germinativo y hacer prueba de placas en cualquier distribuidor Bayer.'
                            )}
                        </Typography>
                        <Typography variant='body1' sx={{ color: 'white !important' }}>
                            {_('Para más información, accedé a los Manuales DPS de ')}
                            <Link
                                className={styles.library}
                                blank
                                to={'library.LibraryDetail'}
                                params={slugProbadorDePlaca}
                            >
                                {_('probador de Placa ')}
                            </Link>
                            {_(' o ')}
                            <Link
                                className={styles.library}
                                blank
                                to={'library.LibraryDetail'}
                                params={slugServicioPostventa}
                            >
                                {_('servicio de post venta ')}
                            </Link>
                            {_(', o bien consultá con tu distribuidor de confianza.')}
                        </Typography>
                    </div>
                )}
            </DPS>
        );
    }

    renderBagInfo() {
        const classNameLT =
            settings.app.theme !== 'cultivio' && settings.app.theme !== 'expertobayer' && !this.state.showHistorical
                ? styles.marginLatijereta
                : '';
        return (
            <div className={classNames(styles.gradiant, classNameLT)}>
                <div
                    className={styles.bagContainer}
                    ref={(el) => {
                        this.batchInfo = el;
                    }}
                >
                    {this.renderBagImage()}
                    {this.renderActions()}
                </div>
            </div>
        );
    }

    renderBagImage() {
        return (
            <div className={styles.bagImageContainer}>
                <div className={styles.bagImageBackground}>
                    <Title className={styles.text} theme={settings.app.theme}>
                        {_('Podés encontrar el número de lote en la bolsa')}
                    </Title>

                    <div
                        className={classNames(
                            styles.bagImage,
                            this.props.brand?.theme && styles[`theme-${this.props.brand.theme}`]
                        )}
                    />
                </div>
            </div>
        );
    }

    renderActions() {
        return (
            <div className={styles.dataContainer}>
                <div>
                    <br />
                    <form onSubmit={this.onSubmit}>
                        <div className={styles.form}>
                            <div className={styles.input}>
                                <TextField
                                    label={_('Número de lote')}
                                    size='small'
                                    variant='outlined'
                                    value={this.state.batchInputValue}
                                    onChange={(e) => this.handleBatchChange(e)}
                                    error={this.state.batchError}
                                    helperText={
                                        this.state.batchError
                                            ? _('No obtuvimos resultados para el lote ingresado.')
                                            : false
                                    }
                                    placeholder='XX0000000'
                                    fullWidth
                                    inputProps={{
                                        maxLength: 20,
                                    }}
                                    FormHelperTextProps={{
                                        style: {
                                            fontSize: 'xx-small',
                                        },
                                    }}
                                    onKeyDown={(e) => this.handleKeyDownNoAction(e)}
                                />
                            </div>
                            <div>
                                <MuiButton
                                    variant='contained'
                                    disabled={!this.state.batchInputValue}
                                    onClick={this.onSubmit}
                                >
                                    {_('Ver info')}
                                </MuiButton>
                            </div>
                        </div>
                    </form>
                    {this.state.batchError ? null : this.props.batchData ? (
                        !this.state.showDPSAnalysis ? (
                            <BatchResultTable data={this.props.batchData} />
                        ) : (
                            <DPSAnalysisTable
                                data={this.props.batchData}
                                onClose={() => this.setState({ showDPSAnalysis: false })}
                            />
                        )
                    ) : null}
                </div>
                {this.renderResults()}
            </div>
        );
    }

    renderResults() {
        const disable = this.state.batchInputValue.length > 0 && !this.state.batchError && !this.state.disableBtn;
        const dpsColor = '#2b618a';
        return (
            <div className={styles.actions}>
                {!this.state.showDPSAnalysis && this.props.internalProfileEnabledOrLT && this.props.country?.code !== 'UY' && (
                    <Button
                        fullWidth
                        variant='contained'
                        sx={{
                            backgroundColor: dpsColor,
                            color: 'white',
                            mb: 2,
                            ':hover': { backgroundColor: dpsColor },
                        }}
                        onClick={() => this.setState({ showDPSAnalysis: true })}
                        disabled={
                            !this.props.batchData ||
                            this.state.batchError ||
                            !this.props.batchData?.response ||
                            !Array.isArray(this.props.batchData.response.dpsAnalysis) ||
                            this.props.batchData.response.dpsAnalysis.length === 0
                        }
                    >
                        {_('Acceder al análisis DPS')}
                    </Button>
                )}
                {this.props.user && (
                    <>
                        <div className={styles.save}>
                            <TextField
                                size='small'
                                variant='outlined'
                                value={this.state.noteInputValue}
                                onChange={(e) => this.handleNoteChange(e)}
                                label={_('Agregar nota (opcional)')}
                                fullWidth={isMobile()}
                                onKeyDown={(e) => this.handleKeyDown(e)}
                                inputProps={{
                                    maxLength: 20,
                                }}
                            />
                            <MuiButton variant='contained' disabled={!disable} onClick={this.handleSaveBatch}>
                                {_('Guardar')}
                            </MuiButton>
                        </div>
                        <div className={styles.saved}>
                            <Button variant='text' onClick={this.showSavedHistorical}>
                                {this.state.showHistorical ? _('Ocultar guardadas') : _('Ver guardadas')}
                            </Button>
                        </div>
                    </>
                )}
            </div>
        );
    }

    renderHistoricalConsultations() {
        const classNameLT =
            settings.app.theme !== 'cultivio' && settings.app.theme !== 'expertobayer' && this.state.showHistorical
                ? styles.marginLatijereta
                : '';
        const dataHistorical = this.props.historicalConsultations;
        if (dataHistorical && dataHistorical.length !== 0) {
            return this.renderTableConsultations(dataHistorical);
        }
        return (
            <div className={classNames(styles.containerSavedTable, classNameLT)}>
                <Title className={styles.textNoHistorical}>
                    {_('Todavía no guardaste ninguna consulta de bolsa.')}
                </Title>
            </div>
        );
    }

    renderTableConsultations(data) {
        const classNameLT =
            settings.app.theme !== 'cultivio' && settings.app.theme !== 'expertobayer' && this.state.showHistorical
                ? styles.marginLatijereta
                : '';
        return (
            <div className={classNames(styles.containerSavedTable, classNameLT)}>
                <Title className={styles.titleLastBatchs}>{_('Últimos lotes guardados')}</Title>
                <table className={styles.tableHistorical}>
                    <thead className={styles.borderBottom}>
                        <tr>
                            <th>
                                <Title size='sm' theme={settings.app.theme}>
                                    {_('Fecha de pedido')}
                                </Title>
                            </th>
                            <th>
                                <Title size='sm' theme={settings.app.theme}>
                                    {_('Nro. de lote')}
                                </Title>
                            </th>
                            {!isMobile() && (
                                <th>
                                    <Title size='sm' theme={settings.app.theme}>
                                        {_('Guardado con nota')}
                                    </Title>
                                </th>
                            )}
                            <th>
                                <Title size='sm' theme={settings.app.theme}>
                                    {_('Acciones')}
                                </Title>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.map((item) => {
                            return (
                                <tr key={item.bagQuality.batch}>
                                    <td>{item.date}</td>
                                    <td>{item.bagQuality.batch}</td>
                                    {!isMobile() && <td> {item.note ? item.note : '-'}</td>}
                                    <td>
                                        <div className={styles.containerSeeAndIcon}>
                                            <MuiButton
                                                sx={{ mr: 2 }}
                                                variant='text'
                                                onClick={() => this.handleSeeBatchHistorical(item.bagQuality.batch)}
                                            >
                                                {_('Ver')}
                                            </MuiButton>
                                        </div>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
        );
    }

    renderFooter() {
        return (
            <div
                className={styles.footer}
                ref={(el) => {
                    this.dpsEnd = el;
                }}
            >
                {this.renderDPS(false)}
            </div>
        );
    }

    // ------ Mobile renders ------

    renderBagInfoMobile() {
        return (
            <div className={styles.containerBagInfoMobile}>
                <Title theme={settings.app.theme} className={styles.titleBagInfoMobile}>
                    {_('Podés encontrar el número de lote en la bolsa')}
                </Title>
                <div className={styles.containerBagImg}>
                    <div
                        className={classNames(
                            styles.imgBagMobile,
                            this.props.brand?.theme && styles[`theme-${this.props.brand.theme}`]
                        )}
                    />
                    <div className={styles.formBagMobile}>
                        <form onSubmit={this.onSubmit}>
                            <Stack spacing={2}>
                                <TextField
                                    label={_('Número de lote')}
                                    size='small'
                                    variant='outlined'
                                    value={this.state.batchInputValue}
                                    onChange={(e) => this.handleBatchChange(e)}
                                    error={this.state.batchError}
                                    helperText={
                                        this.state.batchError
                                            ? _('No obtuvimos resultados para el lote ingresado.')
                                            : false
                                    }
                                    placeholder='XX0000000'
                                    fullWidth
                                    inputProps={{
                                        maxLength: 20,
                                    }}
                                    FormHelperTextProps={{
                                        style: {
                                            fontSize: 'xx-small',
                                        },
                                    }}
                                    onKeyDown={(e) => this.handleKeyDownNoAction(e)}
                                />
                                <MuiButton
                                    variant='contained'
                                    disabled={!this.state.batchInputValue}
                                    onClick={this.onSubmit}
                                >
                                    {_('Ver info')}
                                </MuiButton>
                            </Stack>
                            {this.state.showQRScanner && this.props.qrCornSeedQualityEnabled && (
                                <QRCodeScanner
                                    sx={{ width: '100%', my: 1 }}
                                    qrCodeSuccessCallback={this.onNewScanResult}
                                />
                            )}
                        </form>
                    </div>
                </div>
            </div>
        );
    }

    renderBagResultMobile() {
        return (
            <div
                className={styles.containerBagResultMobile}
                ref={(el) => {
                    this.batchInfo = el;
                }}
            >
                {this.props.batchData &&
                    (!this.state.showDPSAnalysis ? (
                        <BatchResultTable data={this.props.batchData} />
                    ) : (
                        <DPSAnalysisTable
                            data={this.props.batchData}
                            onClose={() => this.setState({ showDPSAnalysis: false })}
                        />
                    ))}
                {this.renderResults()}
            </div>
        );
    }

    renderProbadorPlacaCard() {
        const redirect = () => {
            window.open(
                reverse('library.LibraryDetail', {
                    params: { slug: 'probador-placas-lt' },
                })
            );
        };
        return (
            <Card style={{ width: '100%', margin: '1rem 0 0 0' }}>
                <CardContent>
                    <Title theme={settings.app.theme} color={'dark'} size={isMobile() ? 'sm' : 'md'}>
                        {_('Para más información, accedé al ')}
                        <span
                            style={{ cursor: 'pointer', fontWeight: 'bold', textDecoration: 'none' }}
                            onClick={() => redirect()}
                        >
                            {_('Manual de probador de Placa ')}
                        </span>
                        {_(', o bien consultá con tu distribuidor de confianza.')}
                    </Title>
                </CardContent>
            </Card>
        );
    }

    render() {
        return (
            <Fragment>
                {!isMobile() ? (
                    <Container gradient='center-v2' color='blue'>
                        {this.renderHeader()}
                        {this.renderTitle()}
                        {this.renderBagInfo()}
                        {this.state.showHistorical && this.renderHistoricalConsultations()}
                        {['cultivio', 'expertobayer'].includes(settings.app.theme) &&
                            this.props.country?.code !== 'UY' &&
                            this.renderFooter()}
                        {settings.app.theme === 'latijereta' && this.renderProbadorPlacaCard()}
                    </Container>
                ) : (
                    <Fragment>
                        <Container>
                            {this.renderHeader()}
                            {this.renderTitle()}
                        </Container>
                        <Fragment>
                            {this.renderBagInfoMobile()}
                            {['cultivio', 'expertobayer'].includes(settings.app.theme) &&
                                this.props.country?.code !== 'UY' && (
                                    <div className={styles.dpsMobile}>{this.renderDPS(true)}</div>
                                )}
                            {settings.app.theme === 'latijereta' && this.renderProbadorPlacaCard()}
                        </Fragment>
                        <Container>
                            {this.renderBagResultMobile()}
                            {this.state.showHistorical && this.renderHistoricalConsultations()}
                        </Container>
                        <Fragment>
                            {['cultivio', 'expertobayer'].includes(settings.app.theme) &&
                            this.props.country?.code !== 'UY' &&
                            this.renderFooter()}
                        </Fragment>
                    </Fragment>
                )}
            </Fragment>
        );
    }
}

const BatchResultTable = ({ data }) => {
    const textStyle = data ? styles.data : styles.mockData;

    const descriptions = {
        recommendedPlate: _(
            'Es la placa de siembra que mejor  cumple con las especificaciones citadas en el manual DPS de referencia'
        ),
        pg: _(
            'Análisis de laboratorio estandarizado donde se expone a la semilla de maíz a condición óptimas de temperatura, humedad y sustrato para estimar el máximo potencial de germinación del lote analizado.'
        ),
        doubles: _('Porcentaje de la cantidad de semillas que pasaron en exceso por cada orificio.'),
        failures: _(
            'Porcentaje de la cantidad de semillas que no pasaron por cada orificio de la placa con respecto a la cantidad de orificios totales.'
        ),
        dav: _(
            'Abreviatura del Documento de Autorización de Ventas emitido por INASE (instituto Nacional de las semillas)'
        ),
        feedingIndex: _(
            'Porcentaje de la cantidad de semillas que pasaron por cada orificio de la placa con respecto a la cantidad de orificios totales.'
        ),
        vigor: _('Medida de la capacidad de la semilla para germinar y desarrollarse en condiciones adversas.'),
        vacuum: _(
            'Diferencia de presión generada por el sistema neumático de la sembradora que permite que las semillas se adhieran a los orificios del disco dosificador para ser transportadas y liberadas individualmente al surco.'
        ),
        target: _(
            'Aplicar la presión adecuada para que cada orificio del disco capture y transporte una única semilla, logrando alta singulación y minimizando fallas y duplicaciones.'
        ),
        flowRegulation: _(
            'Ajuste de la sembradora para entregar la cantidad de semillas necesaria para alcanzar la densidad de siembra objetivo (configuración del dosificador, transmisión o sistema electrónico de control).'
        ),
    };

    const renderRow = (
        title: string,
        dataKey: string,
        tooltipText: string | null = null,
        displayContent: React.ReactNode | null = null
    ) => {
        const titleContent = tooltipText ? (
            <Tooltip text={tooltipText}>
                <div className={styles.centerTextIcon}>
                    <span>
                        <Title theme={settings.app.theme}>{title}</Title>
                    </span>
                    <InfoIcon sx={{ fontSize: 20, ml: 0.5, color: (t) => t.palette.primary.main }} />
                </div>
            </Tooltip>
        ) : (
            <Title theme={settings.app.theme}>{title}</Title>
        );

        const value = data?.response?.[dataKey];
        const hasValue = value !== null && value !== undefined && value !== '';

        if (data && !hasValue) {
            return null;
        }

        return (
            <tr>
                <th className={classNames(styles.dataTitle)}>{titleContent}</th>
                <th className={textStyle}>{hasValue ? (displayContent ?? value) : title}</th>
            </tr>
        );
    };

    const DAVContent = () => {
        const dav = data?.response?.dav;
        if (!dav || typeof dav !== 'string') {
            return null;
        }

        const davs = dav.split('/').filter(Boolean);
        if (davs.length === 0) {
            return null;
        }

        return (
            <Stack direction='row' spacing={1} alignItems='center'>
                {davs.map((davItem: string, idx: number) => {
                    return (
                        <Stack key={davItem || idx} direction='row' spacing={0.5} alignItems='center'>
                            <Typography variant='body1' className={styles.data}>
                                {davItem}
                            </Typography>
                            {data?.response?.davsUrls?.[davItem] && (
                                <LaunchIcon
                                    sx={{ fontSize: 20, color: (t: Theme) => t.palette.primary.main }}
                                    className={styles.pointer}
                                    onClick={() => window.open(data?.response?.davsUrls?.[davItem], '_blank')}
                                />
                            )}
                            {idx < davs.length - 1 && (
                                <Typography variant='body1' className={styles.data}>
                                    /
                                </Typography>
                            )}
                        </Stack>
                    );
                })}
            </Stack>
        );
    };

    return (
        <table className={styles.dataTable}>
            <tbody>
                {renderRow(_('Híbrido'), 'hybrid')}
                {renderRow(_('Grado'), 'grade')}
                {renderRow(_('PG (Poder Germinativo)'), 'germinationPower', descriptions.pg)}
                {renderRow(_('Vigor'), 'vigor', descriptions.vigor)}
                {renderRow(_('DAV'), 'dav', descriptions.dav, <DAVContent />)}
                {renderRow(_('Placa recomendada'), 'recommendedPlate', descriptions.recommendedPlate)}
                {renderRow(_('Vacío [in]'), 'vacuum', descriptions.vacuum)}
                {renderRow(_('Objetivo [%]'), 'target', descriptions.target)}
                {renderRow(_('Regulación de caudal'), 'flowRegulation', descriptions.flowRegulation)}
                {renderRow(_('Índice de alimentación'), 'feedingIndex', descriptions.feedingIndex)}
                {renderRow(_('%  Dobles'), 'doubles', descriptions.doubles)}
                {renderRow(_('% Fallos'), 'failures', descriptions.failures)}
            </tbody>
        </table>
    );
};

const DPSAnalysisTable = ({ data, onClose }) => {
    if (!data || !data.response) return null;
    return (
        <Paper sx={{ width: '100%', boxShadow: 'none', pr: 3, pt: 3 }}>
            <Grid container justifyContent='space-between' alignItems='center'>
                <Grid item sx={{ m: 1 }}>
                    <img src={dps} />
                </Grid>
                <Grid item sx={{ m: 1 }}>
                    <Icon type='close2' size='md' onClick={() => onClose()} className={styles.pointer} />
                </Grid>
            </Grid>
            <TableContainer>
                <Table sx={{ borderCollapse: 'separate', borderSpacing: '0 10px' }} aria-label='simple table'>
                    <TableHead sx={{ '& tr th': { border: 0 } }}>
                        <TableRow>
                            <TableCell sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: 12 }}>{_('Lote')}</TableCell>
                            <TableCell sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: 12 }}>{_('Fecha')}</TableCell>
                            <TableCell sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: 12 }}>{_('Laboratorio')}</TableCell>
                            <TableCell sx={{ color: 'rgba(0, 0, 0, 0.5)', fontSize: 12 }}>{_('PG(%)')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(data?.response?.dpsAnalysis || []).map((el, index) => (
                            <TableRow
                                key={index}
                                sx={{
                                    fontSize: 14,
                                    td: {
                                        border: '1px solid #E1E1E1',
                                        borderLeft: 0,
                                        borderRight: 0,
                                    },
                                    'td:first-child': {
                                        borderLeft: '1px solid #E1E1E1',
                                        borderTopLeftRadius: '8px',
                                        borderBottomLeftRadius: '8px',
                                        borderTopRightRadius: 0,
                                        borderBottomRightRadius: 0,
                                    },
                                    'td:last-child': {
                                        borderLeft: 0,
                                        borderRight: '1px solid #E1E1E1',
                                        borderTopLeftRadius: 0,
                                        borderBottomLeftRadius: 0,
                                        borderTopRightRadius: '8px',
                                        borderBottomRightRadius: '8px',
                                    },
                                }}
                            >
                                <TableCell>{data?.response?.batch || '-'}</TableCell>
                                <TableCell>{el?.finishDate || '-'}</TableCell>
                                <TableCell>{el?.laboratory || '-'}</TableCell>
                                <TableCell>{el?.pgPercent || '-'}</TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
};
